/**
 * LoopPanel
 * ---------
 * 
 * August 2026
 */

import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';

const loopWarnings = {
    'iteration_script': function(newIter) { return newIter <= 0 ? "Iteration # must be positive" : null },
    'probability_script': function(newProbs) { return newProbs[0] + newProbs[1] !== 100 ? "Probabilities must sum to 100" : null }
};

/**
 * ActionPanel sub-component for editing a LOOP element's attributes.
 * 
 * @param {Object} config                   - Configuration object for the simulation model. 
 * @param {Function} setConfig              - Setter for config object.
 * @param {Object} action                   - Attributes for action element being edited.
 * @param {Object} configWarnings           - Verification warnings for entity types.
 * @param {Function} updateActionWarnings   - Setter for verification warnings.
 */
function LoopPanel({ config, setConfig, action, configWarnings, updateActionWarnings }) {
    if (action.type !== 'loop') return null;

    /**
     * Updates the loop element's script for iterating the loop body.
     * Available scripts:
     *  - Iterations
     *  - Probability
     * 
     * @param {String} newScript    - Updated script being set.
     * @param {String} gid          - Action's identifier
     */
    const handleLoopScript = (newScript, gid) => {
        updateActionWarnings(gid, 'iteration_script', null);
        updateActionWarnings(gid, 'probability_script', null);

        const newConfig = {...config, actions: {...config.actions, [gid]: {...config.actions[gid], loop_script: [newScript, (newScript === "iteration") ? 1 : [50.0, 50.0]]}}};
        setConfig(newConfig);
    }

    /**
     * Updates the loop element's iteration script.
     * 
     * @param {Number} newValue - Updated number of iterations.
     * @param {String} gid      - Action's identifier.
     */
    const handleLoopIteration = (newValue, gid) => {
        updateActionWarnings(gid, 'iteration_script', loopWarnings['iteration_script'](newValue));

        const newConfig = {...config, actions: {...config.actions, [gid]: {...config.actions[gid], loop_script: ['iteration', newValue]}}};
        setConfig(newConfig);
    }

    /**
     * Updates the loop element's probability script.
     * 
     * @param {Number} newProb  - Updated probability value.
     * @param {String} gid      - Action's identifier.
     * @param {Number} i        - Probability branch index.
     */
    const handleLoopProbability = (newProb, gid, i) => {
        let newProbabilities = [...config.actions[gid].loop_script[1]];
        newProbabilities[i] = newProb;

        updateActionWarnings(gid, 'probability_script', loopWarnings['probability_script'](newProbabilities));
        
        const newConfig = {...config, actions: {...config.actions, [gid]: {...config.actions[gid], loop_script: ['probability', newProbabilities]}}};
        setConfig(newConfig);
    }

    return (
        <div>
            <div style={{ marginBottom: '1rem'}}>
                <label style={{ marginRight: '0.5rem'}}>"LOOP" Script:</label>
                <Dropdown value={action.loop_script[0]} onChange={(event) => handleLoopScript(event.target.value, action.gid)} options={["iteration","probability"]} />
            </div>
            {action.loop_script[0] === 'iteration' && (
                <div>
                    <label style={{ marginRight: '0.5rem'}}>Iteration #:</label>
                    <InputNumber value={action.loop_script[1]} onValueChange={(event) => handleLoopIteration(event.target.value, action.gid)} placeholder="Enter value" min={0} useGrouping={false} invalid={!!configWarnings.actions?.[action.gid]?.iteration_script} inputStyle={{ width: '110px'}}/>
                </div>
            )}
            {action.loop_script[0] === 'probability' && (
                <div>
                    <div style={{ marginBottom: '1rem'}}>
                        <label style={{ marginRight: '0.5rem'}}>Iterate Branch:</label>
                        <InputNumber value={action.loop_script[1][0]} onValueChange={(event) => handleLoopProbability(event.target.value, action.gid, 0)} placeholder="Enter value" min={0} useGrouping={false} invalid={!!configWarnings.actions?.[action.gid]?.probability_script} inputStyle={{ width: '110px', marginRight: '1rem'}}/>
                    </div>
                    <div style={{ marginBottom: '1rem'}}>
                        <label style={{ marginRight: '0.5rem'}}>Exit Branch:</label>
                        <InputNumber value={action.loop_script[1][1]} onValueChange={(event) => handleLoopProbability(event.target.value, action.gid, 1)} placeholder="Enter value" min={0} useGrouping={false}  invalid={!!configWarnings.actions?.[action.gid]?.probability_script} inputStyle={{ width: '110px'}}/>
                    </div>
                    <label>Values must be between 1 and 100.</label>
                </div>
            )}
        </div>
    )
}

export default LoopPanel;